import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Tag, message, Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatFullName } from 'shared/utils';
import { useProfileLang } from 'shared/hooks/profile.hooks';
import { ROUTES } from 'shared/constants/routes';
import {
  EVENTS,
  SCHEDULE_ANALYTICS_VIEW_TYPE,
  SCHEDULE_REPORT_EXPORT_TYPE,
} from 'shared/constants';
import { Button } from 'src/components/design-system';
import { Loader } from 'src/components/core';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { AG_FILTER_TYPES, textWithFallback } from 'src/components/design-system/table';
import scheduleApi from 'shared/api/schedule.service.api';
import { useCheckIns } from 'shared/hooks/attendance.hooks';
import { useScheduleEventDetail } from 'shared/hooks/schedule-events.hooks';
import { useEducationalEventsContext } from '../../educational-events-context';
import {
  attendanceModeLabel,
  getCheckInMethodLabel,
  exportErrorMessage,
} from '../../helpers/event-view.helpers';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import { downloadBlob } from '../../helpers/download-blob';
import { REPORT_EXPORT_FILE, buildReportFileName } from '../../helpers/report-file-name';
import { buildReportExportedProps } from '../../helpers/report-exported-props';
import { ReportExportButton } from './report-export-button';
import { ReportTable, SET_FILTER, RESETTABLE_FILTER } from './report-table';
import { ReportLinkCell } from './report-link-cell';
import styles from './reports.module.scss';

export function EventDrillDown({ eventId, onSelectPerson, onBack }) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const { users } = useEducationalEventsContext();
  const { getValueFromLang } = useProfileLang();
  const { formatDateWithTime, formatTime } = useEducationalEventFormat();

  const detailQuery = useScheduleEventDetail(eventId);
  const event = detailQuery.data;

  const {
    items,
    loading: attendeesLoading,
    error: attendeesError,
  } = useCheckIns(eventId, { all: true });

  const usersById = useMemo(() => Object.fromEntries(users.map(user => [user.id, user])), [users]);

  const rows = useMemo(
    () => (items ?? []).map(item => ({ ...item, user: usersById[item.userId] ?? null })),
    [items, usersById],
  );

  const subtitle = event?.startTime ? formatDateWithTime(event.startTime) : '';

  const columns = useMemo(
    () => [
      {
        headerName: lang.REPORT_COL_NAME,
        colId: 'name',
        sortable: true,
        flex: 2,
        cellClass: styles.primaryCell,
        valueGetter: params =>
          params.data?.user ? formatFullName(params.data.user) || null : null,
        valueFormatter: textWithFallback,
        cellRenderer: ReportLinkCell,
        cellRendererParams: {
          getTo: data =>
            data?.userId ? ROUTES.CONTACTS_PREVIEW.replace(':id', data.userId) : null,
        },
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
        comparator: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
      },
      {
        headerName: lang.REPORT_COL_ROLE,
        colId: 'role',
        sortable: true,
        flex: 1,
        valueGetter: params => getValueFromLang(params.data?.user?.professional?.role),
        valueFormatter: textWithFallback,
        filter: SET_FILTER,
        comparator: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
      },
      {
        headerName: lang.REPORT_COL_TYPE,
        colId: 'attendanceMode',
        sortable: true,
        flex: 1,
        valueGetter: params => params.data?.attendanceMode,
        cellRenderer: params =>
          params.value ? (
            <Tag className={styles.typeTag} variant="filled">
              {attendanceModeLabel(params.value, lang)}
            </Tag>
          ) : (
            '-'
          ),
        filter: SET_FILTER,
        filterParams: { valueFormatter: value => attendanceModeLabel(value, lang) },
      },
      {
        headerName: lang.REPORT_COL_TIME,
        colId: 'checkedInAt',
        sortable: true,
        flex: 1,
        field: 'checkedInAt',
        cellRenderer: params => (params.value ? formatTime(params.value) : '-'),
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
        filterValueGetter: params =>
          params.data?.checkedInAt ? formatTime(params.data.checkedInAt) : '',
      },
      {
        headerName: lang.CHECK_IN_METHOD,
        colId: 'method',
        sortable: true,
        flex: 1,
        valueGetter: params => params.data?.method,
        cellRenderer: params => {
          const label = getCheckInMethodLabel({ method: params.value, lang });
          return label ? (
            <Tag className={styles.typeTag} variant="filled">
              {label}
            </Tag>
          ) : (
            '-'
          );
        },
        filter: SET_FILTER,
        filterParams: {
          valueFormatter: value => getCheckInMethodLabel({ method: value, lang }),
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, getValueFromLang, formatDateWithTime, formatTime],
  );

  const handleExport = async () => {
    try {
      const { blob } = await scheduleApi.exportEventReport({ eventId });
      downloadBlob(blob, buildReportFileName(event?.title, REPORT_EXPORT_FILE.EVENT_SUFFIX));
      analyticsService.track(
        EVENTS.SCHEDULE.REPORT_EXPORTED,
        buildReportExportedProps({
          reportType: SCHEDULE_REPORT_EXPORT_TYPE.EVENT,
          viewType: SCHEDULE_ANALYTICS_VIEW_TYPE.BY_EVENT,
          eventId,
        }),
      );
    } catch (error) {
      monitorService.error('Failed to export event attendance report', { error, eventId });
      message.error(exportErrorMessage(error, lang));
    }
  };

  const hasError = detailQuery.isError || attendeesError;

  const renderContent = () => {
    if (detailQuery.isLoading || attendeesLoading) return <Loader />;
    if (hasError) return <Alert type="error" showIcon message={lang.REPORT_LOAD_FAILED} />;
    return (
      <ReportTable
        columnDefs={columns}
        rowData={rows}
        getRowId={params => params.data.id || `${params.data.userId}-${params.data.checkedInAt}`}
        onRowActivate={data => data?.userId && onSelectPerson(data.userId)}
      />
    );
  };

  return (
    <div className={styles.drillDown}>
      <div className={styles.filterBar}>
        <div className={styles.drillDownTitle}>
          <Button type="link" size="small" className={styles.backLink} onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> {lang.REPORT_BACK_TO_REPORTS}
          </Button>
          <span className={styles.personName}>{event?.title}</span>
          <span className={styles.drillDownSummary}>{subtitle}</span>
        </div>
        <div className={styles.filterSpacer} />
        <ReportExportButton
          onExport={handleExport}
          lang={lang}
          disabled={!rows.length || hasError}
        />
      </div>

      {renderContent()}
    </div>
  );
}
